/* eslint-disable react/no-danger */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import DOMPurify from 'isomorphic-dompurify';
import { useWindowSize } from 'hooks';
import { PortableText } from '@portabletext/react';
import { SEO, Button, Testimonials, SingleColumnCarousel, Card, OtherCards } from 'components';
import { Hero } from 'components/ProductPage/Shared';
import { getAltTagFromFileName, slugify } from 'utils';

function ShowcaseTemplate({ data }) {
  const { heroImage, showcase, gradientPercentage, heroImageOffset, filter } = data;

  const otherShowcases = data.otherShowcases.nodes.map((node) => {
    const { id, title, images: _images } = node;
    const slug = slugify(title);
    const mainImage = _images?.mainImage?.asset;
    return (
      <Card
        key={id}
        id={id}
        slug={slug}
        image={mainImage}
        imageWidth={340}
        imageHeight={380}
        title={title}
        alt={getAltTagFromFileName(mainImage?.originalFilename)}
      />
    );
  });

  const { windowWidth } = useWindowSize();

  const images = showcase.images && [
    showcase.images.mainImage,
    ...showcase.images.additionalImages
  ];

  return (
    <>
      <Hero
        heroImage={heroImage}
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Showcases"
        gradientPercentage={gradientPercentage}
        heroImageOffset={heroImageOffset}
        filter={filter}
        removeProductNameFromPath>
        <h2>Showcase</h2>
      </Hero>
      <Grid>
        <InnerGrid className="fade-in">
          <SingleColumnCarousel images={images} imageWidth={550} imageHeight={700} showIndicators />
          <ShowcaseDetails>
            <h1 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(showcase.title) }} />
            <PortableText value={showcase._rawBody} />
            <StyledButton to="/request-a-quote/">Request A Quote</StyledButton>
          </ShowcaseDetails>
          {windowWidth < 1200 ? (
            <OtherShowcasesMobile>
              <h3>Other Showcases</h3>
              {otherShowcases}
            </OtherShowcasesMobile>
          ) : (
            <OtherCards
              title="Other Showcases"
              cards={data.otherShowcases.nodes}
              backgroundColor="transparent"
            />
          )}
        </InnerGrid>
      </Grid>
      <StyledTestimonials />
    </>
  );
}

const OtherShowcasesMobile = styled.div`
  align-items: center;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-bottom: 18px;

  /* @media (width >= 590px) {
    background-color: var(--connolly-grey);
  } */

  > h3 {
    font-size: 2rem;
    padding: 1.25em 0 0.125em;
  }

  a {
    height: 30.375rem;
    width: 21.25rem;

    .gatsby-image-wrapper {
      height: 23.75rem;
    }
  }
`;

const Grid = styled.section`
  background-color: var(--connolly-grey);
  display: grid;
  grid-column-gap: 1.25rem;
  grid-template-columns: repeat(12, 1fr);
  margin: 0 auto -2rem;
  max-width: 90rem;
  padding: 0;

  @media (width >= 590px) {
    padding: 0 0.625rem;
  }
`;

const InnerGrid = styled.div`
  background-color: #fff;
  border-radius: 0.375rem;
  grid-column: 1/-1;
  grid-template-columns: subgrid;
  grid-template-rows: auto 1fr;
  margin: 0 auto;
  max-width: 590px;
  padding: 1.25rem 1.25rem 2.5rem;
  position: relative;
  row-gap: 1rem;
  top: 0;
  width: 100%;
  z-index: 1;

  @media (width >= 590px) {
    padding: 1.25rem 1.25rem 2.5rem;
  }

  @media (width >= 650px) {
    top: -2.313rem;
  }

  @media (width >= 900px) {
    padding: 1.875rem 1.875rem 2.056rem;
  }

  @media (width >= 1200px) {
    margin: initial;
    display: grid;
    grid-column: 2/12;
    max-width: initial;
  }

  /* Carousel */
  > :nth-child(1) {
    grid-column: 1/6;
  }
`;

const StyledTestimonials = styled(Testimonials)`
  > h3 {
    padding: 3.125em 0 1.25em;
  }
`;

const ShowcaseDetails = styled.section`
  grid-column: 6/12;
  padding: 2.125rem 0 3.75rem;
  position: relative;

  @media (width >= 1200px) {
    padding: 2.125rem 0 3.75rem 1.25rem;
  }

  @media (width >= 1400px) {
    padding: 2.125rem 0 3.75rem 3.125rem;
  }

  h1,
  h4 {
    color: var(--connolly-black);
  }

  h1 {
    font-size: 2rem;
  }

  h4 {
    font-size: 1.125rem;
    margin: 2.44em 0 0.578em;
  }

  ul,
  ol {
    list-style-position: inside;
  }

  h1,
  p,
  a,
  ul,
  ol {
    margin-bottom: 1.25rem;
  }

  a,
  a:visited,
  a:hover,
  a:active {
    color: var(--connolly-black);
  }
`;

const StyledButton = styled(Button)`
  color: #fff !important;
  margin-top: 3.188rem !important;
`;

export const query = graphql`
  query ($id: String!, $idsOfThreeOtherShowcases: [String]!) {
    heroImage: file(relativePath: { eq: "hero-images/showcase/closed-riser-white-stairs.png" }) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    showcase: sanityShowcase(id: { eq: $id }) {
      id
      title
      _rawBody
      body {
        children {
          text
        }
      }
      images {
        mainImage {
          hotspot {
            height
            width
            x
            y
          }
          crop {
            top
            bottom
            left
            right
          }
          asset {
            _id
            originalFilename
            url
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
        additionalImages {
          hotspot {
            height
            width
            x
            y
          }
          crop {
            top
            bottom
            left
            right
          }
          asset {
            _id
            originalFilename
            url
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
      }
    }
    otherShowcases: allSanityShowcase(filter: { id: { in: $idsOfThreeOtherShowcases } }) {
      nodes {
        id
        title
        images {
          mainImage {
            hotspot {
              height
              width
              x
              y
            }
            crop {
              top
              bottom
              left
              right
            }
            asset {
              _id
              originalFilename
              url
              metadata {
                dimensions {
                  width
                  height
                }
              }
            }
          }
        }
      }
    }
  }
`;

export function Head({ data, location }) {
  const { showcase } = data;
  const text = showcase.body?.[0].children?.[0].text;
  const description = `${text.split('.')[0]}.`;
  return (
    <SEO title={`${showcase.title} | Showcase`} description={description}>
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Stairs Ireland",
            "item": "https://www.connollystairs.ie/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Showcases",
            "item": "https://www.connollystairs.ie/showcases/"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "${showcase.title}",
            "item": "https://www.connollystairs.ie${location.pathname}"
          }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default ShowcaseTemplate;
